import { LitElement, html, TemplateResult, nothing } from 'lit';
import { customElement, state, queryAll, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styles } from '../view/styles';
import {
  SearchableAccordionsPanelViewConfigs,
  SearchableViewItem,
  SearchableViewItems,
  SearchableViewItemsGroup,
} from '../types';
import { cloneDeep, isEmpty } from 'lodash';
import { filterTreeNodes } from '../searchFunction';
import { connect } from 'pwa-helpers';
import { translateFn } from '../../../utils/translator.helper';
import CollapsiblePanelController from '../../reactiveControllers/CollapsiblePanelController';
import { GzCollapsiblePanelWebComponent } from '../../gzCollapsiblePanel';
import { Keys } from '../../../types/common/types';
import { RootState, store } from '@nirvana/redux/store';
import {
  AccordionsSearchState,
  activeViewSelector,
  searchStringSelector,
  selectAccordionsSearchState,
  setActiveView,
  setSearchString,
} from '@nirvana/redux/searchable-ui-accordions';

/**
 * @summary Type here the description of this component
 * @tag searchable-accordions-panel
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-searchable-accordions-panel')
export class SearchableAccordionsPanelWebComponent extends connect(store)(LitElement) {
  @property({ type: String, attribute: true, reflect: true })
  public routingPath: string;

  @state()
  private _accordions: SearchableViewItems[] = [];

  @state()
  private activeView: string;

  @state()
  private searchString: string;

  /**
   * The property is used to pass properties that configure the panel component UI (i.e. props for the search input)
   */
  @property({ type: Object, attribute: true, reflect: true })
  searchablePanelViewConfigs: SearchableAccordionsPanelViewConfigs;

  @queryAll('gz-collapsible-panel')
  private collapsiblePanels: HTMLElement[];

  get accordions(): SearchableViewItems[] {
    return this._accordions;
  }

  set accordions(items: SearchableViewItems[]) {
    this._accordions = cloneDeep(items);
    this.buildChildToParentMap();
  }

  /**
   * The ID of the last active panel. It is used to restore the last active panel after the search is cleared.
   * @private
   */
  private lastActivePanelID: string;

  // Mapping of child to parent accordions in order to avoid iterating over the accordions array each time we need to find the parent
  private _childToParentMap: Record<string, string> = {};

  static get styles() {
    return styles;
  }

  private collapsibleController = new CollapsiblePanelController<GzCollapsiblePanelWebComponent>(this);

  stateChanged(rootState: RootState) {
    const appState: AccordionsSearchState = selectAccordionsSearchState(rootState);
    this.searchString = searchStringSelector(appState);
    this.activeView = activeViewSelector(appState);
  }

  private buildChildToParentMap(): void {
    if (!isEmpty(this._childToParentMap)) {
      return;
    }

    this._accordions.forEach(parent => {
      parent.children.forEach(child => {
        this._childToParentMap[child.id] = parent.id;
      });
    });
  }

  public openAccordionPanelById(panelId: string): void {
    const panel: HTMLElement | undefined = Array.from(this.collapsiblePanels).find(
      collapsiblePanel => collapsiblePanel.getAttribute('id') === panelId
    );
    this.openAccordionPanel(panel as GzCollapsiblePanelWebComponent);
  }

  private openAccordionPanelByIndex(panelIndex: number): void {
    const panel = this.collapsiblePanels[panelIndex];
    this.openAccordionPanel(panel as GzCollapsiblePanelWebComponent);
  }

  private openAccordionPanel(panel: GzCollapsiblePanelWebComponent | undefined): void {
    if (panel) {
      this.collapsibleController.collapseAllPanels();
      this.lastActivePanelID = panel.getAttribute('id') as string;
      this.collapsibleController.openPanel(panel as GzCollapsiblePanelWebComponent);
    }
  }

  private getGroupedItems(
    items: SearchableViewItems[]
  ): Record<string, { group?: SearchableViewItemsGroup; items: SearchableViewItems[] }> {
    return items.reduce(
      (
        acc: Record<string, { group?: SearchableViewItemsGroup; items: SearchableViewItems[] }>,
        item: SearchableViewItems
      ) => {
        const groupId = item.group?.id || 'ungrouped';
        if (!acc[groupId]) {
          acc[groupId] = { group: item.group, items: [] };
        }
        acc[groupId].items.push(item);
        return acc;
      },
      {}
    );
  }

  private onTogglePanel(e: Event, firstChild: SearchableViewItems | SearchableViewItem): void {
    this.collapsibleController.togglePanel(e.target as GzCollapsiblePanelWebComponent);

    if ((e as CustomEvent).detail.isOpen && firstChild.uiElementName) {
      this.activeView = firstChild.uiElementName;
      this.onListItemClick(firstChild);
    }
  }

  private onListItemKeydown(
    e: KeyboardEvent,
    viewItem: SearchableViewItem,
    isNode = false,
    isSingleItem = false
  ): void {
    if (e.key === Keys.Enter || e.key === ' ') {
      this.onListItemClick(viewItem, isNode, isSingleItem);
    }
  }

  private restoreLastActivePanel() {
    // get last open panel and search for it in the collapsible panels
    const lastActivePanel: HTMLElement | undefined = Array.from(this.collapsiblePanels).find(
      panel => panel.getAttribute('id') === this.lastActivePanelID
    );

    if (typeof lastActivePanel === undefined) {
      this.openAccordionPanelByIndex(0);
    } else {
      this.collapsibleController.openPanel(lastActivePanel as GzCollapsiblePanelWebComponent);
    }
  }

  private onListItemClick(viewItem: SearchableViewItem, isNode = false, isSingleItem = false): void {
    const parentId = this._childToParentMap[viewItem.id];
    if (parentId) {
      this.lastActivePanelID = parentId;
    }
    if (isNode && !isSingleItem) {
      this.collapsibleController.collapseAllPanels();
    }

    // route to the specified path if exists
    if (viewItem.route) {
      Nav.redirect(this.routingPath + viewItem.route);
      return;
    }

    // set the active view
    if (viewItem.uiElementName) {
      store.dispatch(setActiveView(viewItem.uiElementName));
    }
  }

  private onSearchChange(event): void {
    const searchString: string = event.detail.sourceEvent;
    store.dispatch(setSearchString(searchString));
    this.collapsibleController.resetPanels();

    if (
      this.searchablePanelViewConfigs.searchStringMinLength &&
      searchString.length < this.searchablePanelViewConfigs.searchStringMinLength
    ) {
      setTimeout(() => this.restoreLastActivePanel(), 0);
      return;
    }

    if (isEmpty(searchString)) {
      // ensure that the last active panel is opened after the DOM is fully loaded and all panels are available
      setTimeout(() => this.restoreLastActivePanel(), 0);
    } else {
      setTimeout(() => this.collapsibleController.expandAllPanels(), 0);
    }
  }

  private renderViewPanels(): TemplateResult {
    const filteredItems = this.searchString
      ? filterTreeNodes(this._accordions, this.searchString, this.searchablePanelViewConfigs.searchStringMinLength)
      : this._accordions;
    if (filteredItems.length === 0) {
      return html` <div class="no-results">
        <gz-text fontSize="14">${translateFn('No results found')}</gz-text>
      </div>`;
    }

    const groupedItems = this.getGroupedItems(filteredItems);
    return html`
      ${repeat(
        Object.entries(groupedItems),
        ([groupId, _]) => groupId,
        ([groupId, { group, items }]) => this.renderGroup(items, group)
      )}
    `;
  }

  private renderGroup(items: SearchableViewItems[], group?: SearchableViewItemsGroup): TemplateResult {
    return html`
      <div class="panel-group" role="group" aria-labelledby="${group?.id}">
        ${group
          ? html`<gz-title fontSize="11" fontWeight="600" class="group-title">${group.label.toUpperCase()}</gz-title>`
          : nothing}
        ${repeat(
          items,
          (item, index) => index,
          (item: SearchableViewItems, index: number) => this.renderGroupItems(item)
        )}
      </div>
    `;
  }

  private renderPanelListItemContent(item: SearchableViewItem): TemplateResult {
    const customContent = item.renderer ? item.renderer(item) : nothing;
    return html`
      <gz-tooltip isEllipsis tooltipText="${item.label}">${item.label}</gz-tooltip>
      ${customContent}
    `;
  }

  private renderPanelListItem(item: SearchableViewItem, isNode = false, isSingleItem = false): TemplateResult {
    const activeClass = item.uiElementName && this.activeView === item.uiElementName ? 'active' : '';

    // check if the item is active and if it is, check if the parent id is different from the last active panel id
    if (activeClass) {
      const parentId = this._childToParentMap[item.id];

      // if the parent id is different from the last active panel id, then set the last active panel id to the parent id
      // it means that navigation to this panel was done in a different way than clicking on the panel
      if (parentId !== this.lastActivePanelID) {
        this.lastActivePanelID = parentId;
        this.restoreLastActivePanel();
      }
    }

    const cssClasses = isNode ? 'is-node' : 'is-leaf';
    return html`<div
      role="listitem"
      tabindex="0"
      class=${`panel-list-item content-item ${cssClasses} ${activeClass}`}
      @click="${() => this.onListItemClick(item, isNode, isSingleItem)}"
      @keydown="${e => this.onListItemKeydown(e, item, isNode, isSingleItem)}"
      data-id="${item.uiElementName}"
    >
      ${this.renderPanelListItemContent(item)}
    </div>`;
  }

  private renderCollapsiblePanel(item: SearchableViewItems): TemplateResult {
    return html`
      <gz-collapsible-panel
        @toggle="${e => this.onTogglePanel(e, item.children[0])}"
        @panelHeaderClick="${() => this.onListItemClick(item.children[0])}"
        ?hideToggle="${this.searchString}"
        id="${item.id}"
      >
        <div slot="panel-header" class="panel-list-item header-item">${this.renderPanelListItemContent(item)}</div>
        <div role="list">
          ${repeat(
            item.children,
            (child: SearchableViewItem) => child.id,
            (child, index) => this.renderPanelListItem(child)
          )}
        </div>
      </gz-collapsible-panel>
    `;
  }

  private renderSingleListItem(item: SearchableViewItems): TemplateResult {
    return this.renderPanelListItem(item, true, true);
  }

  private renderGroupItems(item: SearchableViewItems): TemplateResult {
    return item.children && item.children.length > 0
      ? this.renderCollapsiblePanel(item)
      : this.renderSingleListItem(item);
  }

  render() {
    const panelWidth = this.searchablePanelViewConfigs.panelWidth || 240;
    return html`
      <div class="side-menu" style="width: ${panelWidth}px;">
        <div class="side-menu--header">
          <gz-input-search
            searchOnAction
            .placeholder="${this.searchablePanelViewConfigs.searchInputPlaceholder}"
            @search="${this.onSearchChange}"
            .value="${this.searchString}"
          ></gz-input-search>
        </div>
        <div class="side-menu--content">${this.renderViewPanels()}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-searchable-accordions-panel': SearchableAccordionsPanelWebComponent;
  }
}
